import { useCallback, useRef, useState } from 'react'
import { OnboardingCreate, OnboardingStepStatus, ProductOnboardingView } from 'models'
import { ProductsServiceV4 } from 'services/v4/products-v4.service'

/* Onboarding state for the Jira add wizard: holds the server's onboarding view
 * (the source of truth for step states and the optimistic-concurrency revision)
 * and the actions that advance it. */
export const useJiraOnboarding = () => {
  const [view, setViewState] = useState<ProductOnboardingView | null>(null)
  const [applying, setApplying] = useState(false)
  const [loadingView, setLoadingView] = useState(false)

  /* Every request carries the current revision, and the wizard's last step
   * submits and completes within one handler. State updates are not visible
   * until the next render, so the revision is read from a ref that the
   * responses update synchronously — otherwise the second call replays the
   * revision the first one just superseded and the server answers 409. */
  const viewRef = useRef<ProductOnboardingView | null>(null)

  const setView = useCallback((next: ProductOnboardingView | null) => {
    viewRef.current = next
    setViewState(next)
  }, [])

  const createOnboarding = async (onboarding: OnboardingCreate) => {
    setApplying(true)
    try {
      const created = await ProductsServiceV4.createOnboarding(onboarding)
      setView(created)
      return created
    } finally {
      setApplying(false)
    }
  }

  /* Submit a step with the current revision; the returned view is the source of
   * truth for the new step states, the next current_step, and the revision the
   * following request must carry. */
  const submitStep = async (stepKey: string, data: Record<string, any>, skip = false) => {
    const current = viewRef.current
    if (!current?.instance_name) return
    setApplying(true)
    try {
      const updated = await ProductsServiceV4.submitStep(current.instance_name, stepKey, {
        revision: current.revision,
        data,
        skip
      })
      setView(updated)
      return updated
    } finally {
      setApplying(false)
    }
  }

  /* Finish onboarding: provisions the connector and starts the first sync. */
  const completeOnboarding = async () => {
    const current = viewRef.current
    if (!current?.instance_name) return
    setApplying(true)
    try {
      const completed = await ProductsServiceV4.completeOnboarding(current.instance_name, current.revision)
      setView(completed)
      return completed
    } finally {
      setApplying(false)
    }
  }

  /* Rebuild state from the server when returning to an instance-scoped URL
   * (post-Apply navigation and refresh/resume). */
  const loadView = async (instanceName: string) => {
    setLoadingView(true)
    try {
      const loaded = await ProductsServiceV4.getOnboarding(instanceName)
      setView(loaded)
      return loaded
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingView(false)
    }
  }

  const stepStatus = (key: string): OnboardingStepStatus | undefined => view?.steps?.find((s) => s.key === key)?.status

  const isStepCompleted = (key: string) => stepStatus(key) === 'completed'

  /* A later step becomes interactive once onboarding exists and it is the
   * server's current step (or already completed); otherwise it stays locked. */
  const isStepUnlocked = (key: string) => !!view && (key === view.current_step || isStepCompleted(key))

  /* Save is the wizard's ending, so it waits for every step the server declares. */
  const areAllStepsCompleted =
    !!view && !!view.steps?.length && view.steps.every((s) => s.status === 'completed' || s.status === 'skipped')

  return {
    view,
    setView,
    applying,
    loadingView,
    createOnboarding,
    submitStep,
    completeOnboarding,
    areAllStepsCompleted,
    loadView,
    stepStatus,
    isStepCompleted,
    isStepUnlocked
  }
}
